import React, { useState, useEffect } from 'react';

const MoroccoJourneyAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}` };

  const [moroccoGameSettings, setMoroccoGameSettings] = useState({
    is_visible: true,
    is_pro: false,
    name: 'Morocco Journey',
    description: 'Explore Morocco, collect gems, and learn Darija through conversations!'
  });
  const [savingCardText, setSavingCardText] = useState(false);
  const [loadingMoroccoGameSettings, setLoadingMoroccoGameSettings] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const gameSettingsRes = await fetch(`${API}/game/settings`, {
        headers: authHeaders
      });
      if (gameSettingsRes.ok) {
        const gameSettingsData = await gameSettingsRes.json();
        setMoroccoGameSettings({
          is_visible: gameSettingsData.is_visible ?? true,
          is_pro: gameSettingsData.is_pro ?? false,
          name: gameSettingsData.name || 'Morocco Journey',
          description: gameSettingsData.description || 'Explore Morocco, collect gems, and learn Darija through conversations!'
        });
      }
    } catch (err) {
      console.error("Error loading game settings:", err);
    }
  };

  return (
                <div className="space-y-6">
                  <div className={`p-6 rounded-xl border-2 ${
                    isDarkMode
                      ? 'bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-purple-500/30'
                      : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200'
                  }`}>
                    <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      🏜️ Morocco Journey Game Editor
                    </h3>
                    <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Design characters, conversations, and hidden gems for the Morocco Journey adventure game.
                    </p>

                    {/* Action Buttons Row */}
                    <div className="flex flex-wrap gap-4 items-center">
                      <button
                        onClick={() => window.open('/game-editor', '_blank')}
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-indigo-700 transition-all shadow-lg"
                      >
                        🎨 Open Game Editor
                      </button>

                      {/* Hidden/Visible Toggle */}
                      <button
                        onClick={async () => {
                          try {
                            setLoadingMoroccoGameSettings(true);
                            const newVisibility = !moroccoGameSettings.is_visible;
                            const response = await fetch(`${API}/game/toggle-visibility?is_visible=${newVisibility}`, {
                              method: 'POST',
                              headers: authHeaders
                            });
                            if (response.ok) {
                              const data = await response.json();
                              setMoroccoGameSettings(prev => ({ ...prev, is_visible: data.is_visible }));
                              showNotification(data.message, 'success');
                            } else {
                              showNotification('Failed to toggle visibility', 'error');
                            }
                          } catch (err) {
                            showNotification('Error: ' + err.message, 'error');
                          } finally {
                            setLoadingMoroccoGameSettings(false);
                          }
                        }}
                        disabled={loadingMoroccoGameSettings}
                        className={`px-5 py-3 rounded-lg font-semibold transition-all shadow-lg flex items-center gap-2 ${
                          moroccoGameSettings.is_visible
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                            : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700'
                        } ${loadingMoroccoGameSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {moroccoGameSettings.is_visible ? '👁️ Visible' : '👁️‍🗨️ Hidden'}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          moroccoGameSettings.is_visible
                            ? 'bg-green-700/50'
                            : 'bg-gray-700/50'
                        }`}>
                          {moroccoGameSettings.is_visible ? 'ON' : 'OFF'}
                        </span>
                      </button>

                      {/* Free/Pro Toggle */}
                      <button
                        onClick={async () => {
                          try {
                            setLoadingMoroccoGameSettings(true);
                            const newProStatus = !moroccoGameSettings.is_pro;
                            const response = await fetch(`${API}/game/toggle-pro?is_pro=${newProStatus}`, {
                              method: 'POST',
                              headers: authHeaders
                            });
                            if (response.ok) {
                              const data = await response.json();
                              setMoroccoGameSettings(prev => ({ ...prev, is_pro: data.is_pro }));
                              showNotification(data.message, 'success');
                            } else {
                              showNotification('Failed to toggle Pro status', 'error');
                            }
                          } catch (err) {
                            showNotification('Error: ' + err.message, 'error');
                          } finally {
                            setLoadingMoroccoGameSettings(false);
                          }
                        }}
                        disabled={loadingMoroccoGameSettings}
                        className={`px-5 py-3 rounded-lg font-semibold transition-all shadow-lg flex items-center gap-2 ${
                          moroccoGameSettings.is_pro
                            ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white hover:from-amber-600 hover:to-yellow-700'
                            : 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:from-blue-600 hover:to-cyan-700'
                        } ${loadingMoroccoGameSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {moroccoGameSettings.is_pro ? '🔒 Pro' : '🆓 Free'}
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          moroccoGameSettings.is_pro
                            ? 'bg-amber-700/50'
                            : 'bg-blue-700/50'
                        }`}>
                          {moroccoGameSettings.is_pro ? 'PAID' : 'FREE'}
                        </span>
                      </button>
                    </div>

                    {/* Game Card Text */}
                    <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-black/20' : 'bg-white/50'}`}>
                      <h4 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🏷️ Game Card Text</h4>
                      <div className="space-y-3">
                        <div>
                          <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Name</label>
                          <input
                            value={moroccoGameSettings.name || ''}
                            onChange={e => setMoroccoGameSettings(prev => ({ ...prev, name: e.target.value }))}
                            className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
                          />
                        </div>
                        <div>
                          <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                          <input
                            value={moroccoGameSettings.description || ''}
                            onChange={e => setMoroccoGameSettings(prev => ({ ...prev, description: e.target.value }))}
                            className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
                          />
                        </div>
                        <button
                          disabled={savingCardText}
                          onClick={async () => {
                            setSavingCardText(true);
                            try {
                              const res = await fetch(`${API}/game/settings`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json', ...authHeaders },
                                body: JSON.stringify({ name: moroccoGameSettings.name, description: moroccoGameSettings.description })
                              });
                              if (res.ok) showNotification('Game card text saved!', 'success');
                              else showNotification('Failed to save', 'error');
                            } catch (e) { showNotification('Error saving', 'error'); }
                            finally { setSavingCardText(false); }
                          }}
                          className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold"
                        >
                          {savingCardText ? 'Saving...' : 'Save Card Text'}
                        </button>
                      </div>
                    </div>

                    {/* Settings Info */}
                    <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-black/20' : 'bg-white/50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <strong>👁️ Visible/Hidden:</strong> When hidden, the game will not appear on the /game page.<br/>
                        <strong>🔒 Pro/Free:</strong> When Pro, users will see a lock icon and need a paid membership to play.
                      </p>
                    </div>
                  </div>
                </div>
  );
};

export default MoroccoJourneyAdmin;
